import React, { useState, useEffect, useCallback } from 'react';

export default function StoryViewer({ stories, startIndex, onClose, onDelete, onIndexChange }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const story = stories[currentIndex];

  const goNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
      onIndexChange(currentIndex + 1);
    } else {
      onClose();
    }
  }, [currentIndex, stories.length, onIndexChange, onClose]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  useEffect(() => {
    setProgress(0);
    const timer = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          goNext();
          return 0;
        }
        return p + 2;
      });
    }, 100);
    return () => clearInterval(timer);
  }, [currentIndex, goNext]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  if (!story) return null;

  return (
    <div className="story-viewer-overlay" onClick={onClose}>
      <div className="story-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="story-progress-bar">
          {stories.map((_, i) => (
            <div key={i} className="story-progress-segment">
              <div className="story-progress-fill"
                style={{ width: i < currentIndex ? '100%' : i === currentIndex ? `${progress}%` : '0%' }} />
            </div>
          ))}
        </div>
        <div className="story-header">
          <div className="story-user-info">
            <img src={story.userAvatarUrl || '/cat.svg'} alt="" className="story-viewer-avatar" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
            <span className="story-viewer-name">{story.userDisplayName}</span>
            <span className="story-viewer-time">
              {new Date(story.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="story-header-actions">
            <button className="story-action-btn" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="story-content">
          {story.bgColor ? (
            <div className="story-image" style={{ background: story.bgColor, width: '100%', height: '100%' }} />
          ) : (
            <img src={story.imageUrl} alt="" className="story-image" />
          )}
          {story.textContent && (
            <div className="story-text-overlay" style={{
              color: story.textColor,
              fontSize: `${story.textSize}px`,
              left: `${story.textX}%`,
              top: `${story.textY}%`,
              transform: `translate(-50%, -50%) rotate(${story.textRotation}deg)`,
            }}>
              {story.textContent}
            </div>
          )}
          {story.emojiContent && (
            <div className="story-emoji-overlay" style={{
              fontSize: `${story.emojiSize ?? 40}px`,
              left: `${story.emojiX ?? 50}%`,
              top: `${story.emojiY ?? 50}%`,
              transform: `translate(-50%, -50%) rotate(${story.emojiRotation ?? 0}deg)`,
            }}>
              {story.emojiContent}
            </div>
          )}
        </div>
        <div className="story-nav-zones">
          <div className="story-nav-prev" onClick={goPrev}></div>
          <div className="story-nav-next" onClick={goNext}></div>
        </div>
      </div>
    </div>
  );
}
